import { QueryClient } from '@tanstack/react-query'

import config from '@config'

export type BrowserTheme = 'server' | 'dark' | 'light'

export type BrowserThemeNonServer = Exclude<BrowserTheme, 'server'>

export type UserTheme = 'auto' | BrowserTheme

export type UserThemeNonServer = Exclude<UserTheme, 'server'>

export type AppTheme = BrowserTheme

export type AppThemeNonServer = Exclude<AppTheme, 'server'>

const {
  theme: { windowKey: themeWindowKey, autoThemeStyleElementId },
} = config

const autoThemeStylesInjection = (mode: 'add' | 'remove'): void => {
  const root = document.documentElement
  if (mode === 'add') {
    root.style.setProperty('--is-auto-theme', 'initial')
    const styleElement = document.createElement('style')
    styleElement.id = autoThemeStyleElementId
    styleElement.innerHTML =
      '@media (prefers-color-scheme: dark) { :root { --is-light-theme: ; --is-dark-theme: initial; } }'
    const head = document.head || document.getElementsByTagName('head')[0]
    head.appendChild(styleElement)
  } else if (mode === 'remove') {
    root.style.removeProperty('--is-auto-theme')
    const styleElement = document.getElementById(autoThemeStyleElementId)
    if (styleElement) {
      styleElement.remove()
    }
  }
}

const updateThemeCSS = (theme: 'dark' | 'light' | 'auto'): void => {
  try {
    const root = document.documentElement
    if (theme === 'auto') {
      autoThemeStylesInjection('add')

      // Reset inline styles
      root.style.removeProperty('--is-light-theme')
      root.style.removeProperty('--is-dark-theme')
    } else if (theme === 'dark') {
      root.style.setProperty('--is-light-theme', ' ')
      root.style.setProperty('--is-dark-theme', 'initial')

      // Remove auto dark mode media query
      autoThemeStylesInjection('remove')
    } else {
      root.style.setProperty('--is-light-theme', 'initial')
      root.style.setProperty('--is-dark-theme', ' ')

      // Remove auto dark mode media query
      autoThemeStylesInjection('remove')
    }
  } catch {}
}

const updateUserTheme = (
  queryClient: QueryClient,
  newUserTheme: UserThemeNonServer
): void => {
  const existingUserTheme = queryClient.getQueryData<UserTheme>([
    'theme',
    'user',
  ]) as UserTheme

  if (existingUserTheme === newUserTheme) {
    return
  }

  updateThemeCSS(newUserTheme)

  try {
    localStorage.setItem(themeWindowKey, newUserTheme)
  } catch {}

  queryClient.setQueryData<UserTheme>(['theme', 'user'], newUserTheme)
  queryClient.invalidateQueries({
    queryKey: ['theme', 'user'],
    exact: true,
  })
}

const updateBrowserTheme = (
  queryClient: QueryClient,
  newBrowserTheme: BrowserThemeNonServer
): void => {
  const existingBrowserTheme = queryClient.getQueryData<BrowserTheme>([
    'theme',
    'browser',
  ]) as BrowserTheme

  if (newBrowserTheme === existingBrowserTheme) {
    return
  }

  queryClient.setQueryData<BrowserThemeNonServer>(
    ['theme', 'browser'],
    newBrowserTheme
  )
  queryClient.invalidateQueries({
    queryKey: ['theme', 'browser'],
    exact: true,
  })
}

const updateAppTheme = (queryClient: QueryClient): void => {
  const browserTheme = queryClient.getQueryData<BrowserTheme>([
    'theme',
    'browser',
  ]) as BrowserTheme

  const userTheme = queryClient.getQueryData<UserTheme>([
    'theme',
    'user',
  ]) as UserTheme

  const existingAppTheme = queryClient.getQueryData<AppTheme>([
    'theme',
    'app',
  ]) as AppTheme

  let derivedAppTheme: AppTheme

  if ([browserTheme, userTheme].includes('server')) {
    derivedAppTheme = 'server'
  } else if (userTheme !== 'auto') {
    derivedAppTheme = userTheme
  } else {
    derivedAppTheme = browserTheme
  }

  if (existingAppTheme !== derivedAppTheme) {
    queryClient.setQueryData<AppTheme>(['theme', 'app'], derivedAppTheme)
    queryClient.invalidateQueries({
      queryKey: ['theme', 'app'],
      exact: true,
    })
  }
}

export { updateUserTheme, updateBrowserTheme, updateAppTheme, updateThemeCSS }
